// src/components/common/AdBanner.js
// Bannière publicitaire (publicités sponsorisées) — design futuriste
// FR / AR (RTL, police Cairo) — robuste contre les valeurs vides ("null", "NULL", "undefined")
// orientation="horizontal" (défaut) | "vertical" (pour les barres latérales) — v1.169
// Maxula

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiX, FiArrowRight, FiArrowLeft } from 'react-icons/fi';
import { adAPI } from '../../services/api';
import { normalizeImagePath } from '../../utils/imageUtils';
import { useLanguage } from '../../i18n/LanguageContext';

// Lien interne (route React) vs lien externe (http...)
const isInternal = (url) =>
  typeof url === 'string' && url.startsWith('/') && !url.startsWith('//');

// Convertit un lien ABSOLU pointant vers localhost / une IP de dev / le domaine
// courant en CHEMIN INTERNE relatif. Corrige les pubs créées en local dont le
// `link` a été enregistré en dur (ex: "http://localhost:3000/deals/xxx") et qui,
// en production, redirigeaient vers localhost. Les vrais liens externes (autre
// domaine) restent externes et s'ouvrent dans un nouvel onglet.
const toRoute = (raw) => {
  const s = clean(raw);
  if (!s) return '/deals';
  if (isInternal(s)) return s; // déjà interne
  try {
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : 'http://localhost';
    const u = new URL(s, origin);
    const host = (u.hostname || '').toLowerCase();
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '0.0.0.0' || host.endsWith('.local');
    const isSameOrigin = (typeof window !== 'undefined' && window.location) && host === window.location.hostname.toLowerCase();
    if (isLocal || isSameOrigin) {
      return (u.pathname + u.search + u.hash) || '/deals';
    }
    return s; // vrai lien externe
  } catch (e) {
    return s;
  }
};

// Nettoie une valeur : renvoie une chaîne utilisable OU null.
// Neutralise les fausses valeurs stockées en base : "null", "NULL", "undefined", "", espaces.
const clean = (v) => {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  if (!s) return null;
  const low = s.toLowerCase();
  if (low === 'null' || low === 'undefined' || low === 'nan' || low === 'false') return null;
  return s;
};

const AdBanner = ({ placement = 'home_top', orientation = 'horizontal', className = '' }) => {
  const { t, isRTL } = useLanguage();
  const [ads, setAds] = useState([]);
  const [closed, setClosed] = useState(false);
  const [index, setIndex] = useState(0);
  const vertical = orientation === 'vertical';

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await adAPI.getActive(placement);
        if (mounted) setAds(Array.isArray(res.data?.data) ? res.data.data : []);
      } catch (e) {}
    })();
    return () => { mounted = false; };
  }, [placement]);

  // On ne garde que les pubs réellement affichables (titre OU image valide).
  const usableAds = useMemo(
    () => ads.filter((a) => clean(a?.title) || clean(a?.image)),
    [ads]
  );

  // rotation douce si plusieurs pubs
  useEffect(() => {
    if (usableAds.length <= 1) return;
    const len = usableAds.length;
    const tmr = setInterval(() => setIndex((i) => (i + 1) % len), 6000);
    return () => clearInterval(tmr);
  }, [usableAds.length]);

  if (closed || !usableAds.length) return null;

  const ad = usableAds[index % usableAds.length];

  // Valeurs nettoyées (aucune n'affichera jamais "null"/"NULL")
  const title = clean(ad.title);
  const advertiser = clean(ad.advertiser);
  const badge = clean(ad.badge);
  const ctaLabel = clean(ad.ctaLabel) || t('ad.cta');
  const imgRaw = clean(ad.image);
  const img = imgRaw ? normalizeImagePath(imgRaw) : null;
  const target = toRoute(ad.link);

  const onClick = () => { try { adAPI.trackClick(ad._id || ad.id); } catch (e) {} };
  const Arrow = isRTL ? FiArrowLeft : FiArrowRight;

  const ctaClasses =
    'inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-bold ' +
    'text-[#e11d7b] shadow-lg shadow-pink-900/30 transition hover:-translate-y-0.5 ' +
    'hover:shadow-pink-500/40';

  const cta = isInternal(target) ? (
    <Link to={target} onClick={onClick} className={ctaClasses}>
      <span>{ctaLabel}</span>
      <Arrow size={16} />
    </Link>
  ) : (
    <a href={target} target="_blank" rel="noreferrer" onClick={onClick} className={ctaClasses}>
      <span>{ctaLabel}</span>
      <Arrow size={16} />
    </a>
  );

  // ====================== VARIANTE VERTICALE (barre latérale) ======================
  if (vertical) {
    return (
      <div className={`relative w-full ${className}`}>
        <div className="group relative overflow-hidden rounded-2xl bg-gradient-to-b from-[#0b1026] via-[#3b1d6e] to-[#e11d7b] shadow-xl ring-1 ring-white/10">
          <div
            className="pointer-events-none absolute inset-0 opacity-25"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.18) 1px, transparent 0)',
              backgroundSize: '22px 22px'
            }}
          />
          {img && (
            <div className="relative h-40 w-full overflow-hidden">
              <img src={img} alt={title || ''} className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-[#3b1d6e] to-transparent" />
              {badge && (
                <span className="absolute right-2 top-2 inline-flex items-center rounded-full bg-gradient-to-br from-red-500 to-rose-600 px-2.5 py-1 text-xs font-extrabold uppercase text-white shadow-lg ring-2 ring-white/30">
                  {badge}
                </span>
              )}
            </div>
          )}
          <div className="relative z-10 p-4">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur">
              {t('ad.sponsored')}
            </span>
            {title && (
              <h3 className="mt-2 text-base font-extrabold leading-tight text-white" style={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {title}
              </h3>
            )}
            {advertiser && <p className="mt-1 truncate text-xs text-white/70">{advertiser}</p>}
            <div className="mt-3">{cta}</div>
          </div>
          <button
            onClick={() => setClosed(true)}
            aria-label={t('ad.close')}
            className="absolute right-2 top-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-black/30 text-white/80 backdrop-blur transition hover:bg-black/60 hover:text-white"
          >
            <FiX size={13} />
          </button>
        </div>
      </div>
    );
  }

  // ====================== VARIANTE HORIZONTALE (défaut) ======================
  return (
    <div className={`relative w-full ${className}`}>
      <div
        className="group relative h-[140px] sm:h-[168px] overflow-hidden rounded-2xl
                   bg-gradient-to-r from-[#0b1026] via-[#3b1d6e] to-[#e11d7b]
                   shadow-xl ring-1 ring-white/10"
      >
        {/* Trame futuriste (points) */}
        <div
          className="pointer-events-none absolute inset-0 opacity-25"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.18) 1px, transparent 0)',
            backgroundSize: '22px 22px'
          }}
        />
        {/* Halos lumineux */}
        <div className="pointer-events-none absolute -left-10 -top-12 h-44 w-44 rounded-full bg-fuchsia-500/30 blur-3xl" />
        <div className="pointer-events-none absolute right-1/3 top-1/2 h-40 w-40 -translate-y-1/2 rounded-full bg-indigo-500/20 blur-3xl" />

        <div className="relative z-10 flex h-full items-center gap-4 px-5 sm:px-8">
          {/* Texte + CTA */}
          <div className="min-w-0 flex-1">
            <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-white backdrop-blur">
              {t('ad.sponsored')}
            </span>
            {title && (
              <h3
                className="mt-2 text-lg font-extrabold leading-tight text-white sm:text-2xl"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {title}
              </h3>
            )}
            {advertiser && (
              <p className="mt-1 truncate text-xs text-white/70 sm:text-sm">{advertiser}</p>
            )}
            <div className="mt-3">{cta}</div>
          </div>

          {/* Image produit + badge réduction */}
          {img && (
            <div className="relative hidden h-full w-[42%] max-w-[440px] shrink-0 sm:block">
              <img
                src={img}
                alt={title || ''}
                className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
              />
              {/* Fondu pour intégrer l'image au fond (RTL-aware) */}
              <div
                className={`pointer-events-none absolute inset-y-0 w-24 ${
                  isRTL
                    ? 'right-0 bg-gradient-to-l from-[#3b1d6e] via-[#3b1d6e]/40 to-transparent'
                    : 'left-0 bg-gradient-to-r from-[#3b1d6e] via-[#3b1d6e]/40 to-transparent'
                }`}
              />
              {/* Badge réduction */}
              {badge && (
                <span className="absolute right-3 top-3 inline-flex items-center rounded-full bg-gradient-to-br from-red-500 to-rose-600 px-3 py-1.5 text-sm font-extrabold uppercase tracking-wide text-white shadow-lg shadow-red-900/40 ring-2 ring-white/30">
                  {badge}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Fermer */}
        <button
          onClick={() => setClosed(true)}
          aria-label={t('ad.close')}
          className="absolute right-2 top-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-black/30 text-white/80 backdrop-blur transition hover:bg-black/60 hover:text-white"
        >
          <FiX size={13} />
        </button>
      </div>

      {usableAds.length > 1 && (
        <div className="mt-2 flex justify-center gap-1.5">
          {usableAds.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              aria-label={`${t('ad.sponsored')} ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${
                i === index ? 'w-5 bg-[#e11d7b]' : 'w-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdBanner;
